import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { addVilatteToCart, VilatteCartLink } from './VilatteCart';

const rub = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });
const PRICE_NOTE = 'Цена окончательная до Новосибирска. Доставка в другие регионы оплачивается отдельно по тарифам службы доставки.';

const VilatteProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [added, setAdded] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const controller = new AbortController();
    setLoading(true); setAdded('');
    fetch(`/api/vilatte/products/${encodeURIComponent(productId || '')}`, { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Товар не найден')))
      .then((payload) => {
        if(controller.signal.aborted)return;
        if(payload.product.slug && productId!==payload.product.slug){navigate(payload.product.product_url,{replace:true});return;}
        setProduct(payload.product);
        setSelectedSize(productSizes(payload.product)[0]?.label || '');
        setError('');
      })
      .catch((requestError) => { if (requestError.name !== 'AbortError') setError(requestError.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [productId, navigate]);

  useEffect(() => {
    fetch('/api/vilatte/config').then((response) => response.ok ? response.json() : {}).then(setConfig).catch(() => setConfig({}));
  }, []);

  const mediaPhotos = useMemo(() => [
    ...(product?.images || []).map((image) => ({ ...image, kind: 'model' })),
    ...(product?.detail_images || []).map((image) => ({ ...image, kind: 'detail' }))
  ], [product]);
  const sizes = productSizes(product);
  const sizeGroups = groupSizes(sizes);
  const colorVariants = product?.color_variants || [];
  const stockFresh=Date.now()-Date.parse(product?.stock_verified_at || '') <= 60*60000;

  return (
    <main className="vilatte-product-page">
      <div className="container"><div className="vilatte-product-toolbar">
        <button className="vilatte-product-back" type="button" onClick={() => navigate('/vilatte')}>← Вернуться в каталог</button>
        <VilatteCartLink /></div>
        {loading ? <div className="vilatte-state">Загружаем товар…</div> : error || !product ? <div className="vilatte-state is-error"><h1>Товар не найден</h1><p>{error}</p></div> : (
          <div className="vilatte-product-layout">
            <div className="vilatte-product-media">
              <section key={product.external_id} className="vilatte-product-gallery" aria-label="Фотографии товара — листайте влево и вправо" tabIndex={0}>
                {mediaPhotos.map((image, index) => <figure className={`is-${image.kind}`} key={image.sha256 || image.url}><img src={image.url} alt={`${product.title}, фото ${index + 1}`} loading={index === 0 ? 'eager' : 'lazy'} /></figure>)}
              </section>
            </div>

            <aside className="vilatte-product-info">
              <>
                <div className="vilatte-product-labels">{product.is_new && <span>NEW</span>}<p>VILATTE</p></div>
                <h1>{product.title}</h1>
                <dl className="vilatte-product-meta">
                  <div><dt>Артикул</dt><dd>{product.sku}</dd></div>
                  {product.color && <div><dt>Цвет</dt><dd>{product.color}</dd></div>}
                </dl>
                <div className="vilatte-product-price"><strong>{rub.format(product.price_rub)}</strong>{product.oldprice_rub > product.price_rub && <del>{rub.format(product.oldprice_rub)}</del>}</div>
                <section className="vilatte-product-sizes">
                  <h2>Выберите размер</h2>
                  <div className="vilatte-size-groups">{sizeGroups.map((group) => <div className="vilatte-size-group" key={group.height || 'regular'}>
                    {group.height && <p>Рост {group.height}</p>}
                    <div>{group.sizes.map((size) => <button type="button" key={size.label} className={selectedSize === size.label ? 'is-selected' : ''} onClick={() => setSelectedSize(size.label)} title={size.quantity == null ? 'В наличии' : `В наличии: ${size.quantity} шт.`}>{size.base}</button>)}</div>
                  </div>)}</div>
                  {selectedSize && <p>{stockFresh ? stockText(sizes.find((size) => size.label === selectedSize)) : 'Наличие уточняется. Корзина сохранится до обновления.'}</p>}
                </section>
                {colorVariants.length > 1 && <section className="vilatte-product-colors">
                  <h2>Цвета</h2>
                  <div>{colorVariants.map((variant) => <button type="button" key={variant.external_id} className={variant.is_current ? 'is-current' : ''} onClick={() => !variant.is_current && navigate((variant.product_url || `/vilatte/${encodeURIComponent(variant.external_id)}`))} title={variant.color || 'Другой цвет'} aria-label={`${variant.color || 'Другой цвет'}${variant.is_current ? ', выбран' : ''}`}>
                    <img src={variant.main_image} alt="" loading="lazy" />
                  </button>)}</div>
                </section>}
                <button className="vilatte-order-button" type="button" disabled={!selectedSize} onClick={() => {const problem=addVilatteToCart(product,selectedSize);setAdded(problem || 'Добавлено в корзину');}}>Добавить в корзину</button>
                {added && <p className="vlt-cart-added" role="status">{added}<Link to="/vilatte/cart">Перейти в корзину →</Link></p>}
                <p className="vilatte-price-note">{PRICE_NOTE}</p>
                {product.description && <section className="vilatte-product-copy"><h2>Описание</h2><p>{product.description}</p></section>}
                {product.composition && <section className="vilatte-product-copy"><h2>Состав</h2><p>{product.composition}</p></section>}
                <BotLinks config={config} />
              </>
            </aside>
            {(product.size_chart_images || []).length > 0 && <section className="vilatte-product-sizechart"><h2>Таблица размеров</h2>{product.size_chart_images.map((image, index) => <img key={image.sha256 || image.url} src={image.url} alt={`Таблица размеров ${index + 1}`} loading="lazy" />)}</section>}
          </div>
        )}
      </div>
    </main>
  );
};

const BotLinks = ({ config }) => {
  if (!config.telegram_bot_url && !config.max_bot_url) return null;
  return <div className="vilatte-bot-links"><span>Или открыть бот:</span>{config.telegram_bot_url && <a href={config.telegram_bot_url} target="_blank" rel="noreferrer">Telegram</a>}{config.max_bot_url && <a href={config.max_bot_url} target="_blank" rel="noreferrer">MAX</a>}</div>;
};

function productSizes(product) {
  if (!product) return [];
  if ((product.sizes || []).length) return product.sizes;
  return (product.available_sizes || []).map((label) => ({ label, quantity: null }));
}

function groupSizes(sizes) {
  const groups = new Map();
  for (const size of sizes) {
    const match = String(size.label || '').trim().match(/^(.+?)\s*\/\s*(\d{3})$/);
    const base = match ? match[1] : size.label;
    const height = match ? match[2] : '';
    if (!groups.has(height)) groups.set(height, []);
    groups.get(height).push({ ...size, base });
  }
  return [...groups.entries()]
    .sort(([a], [b]) => Number(a || 0) - Number(b || 0))
    .map(([height, entries]) => ({
      height,
      sizes: entries.sort((a, b) => String(a.base).localeCompare(String(b.base), 'ru', { numeric: true }))
    }));
}

function stockText(size) {
  if (!size) return '';
  return size.quantity == null ? 'Размер в наличии' : `В наличии: ${size.quantity} шт.`;
}

export default VilatteProductPage;
